import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useModal } from '../components/useModal';
import apiService from '../services/apiService';
import './RegisterPage.css';

export default function RegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess, ModalComponent } = useModal();
  
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      // Not using standard API service for auth headers because the user is not logged in.
      // Or maybe apiService works without auth headers. We'll try.
      const res = await fetch(`/api/auth/registration-link/${token}`);
      const data = await res.json();
      
      if (res.ok && data.valid) {
        setIsValid(true);
      } else {
        setIsValid(false);
        showError('Ungültiger Link', data.error || 'Der Registrierungs-Link ist ungültig oder abgelaufen.');
      }
    } catch (err) {
      console.error('Error validating token:', err);
      setIsValid(false);
      showError('Fehler', 'Verbindung zum Server fehlgeschlagen.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstname.trim() || !lastname.trim() || !email.trim() || !password.trim()) {
      showError('Fehlende Eingaben', 'Bitte füllen Sie alle Felder aus.');
      return;
    }

    if (password.length < 6) {
      showError('Passwort zu kurz', 'Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/register-with-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, firstname, lastname, email, password })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        showSuccess('Erfolgreich', 'Sie haben sich erfolgreich registriert. Sie können sich nun anmelden.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        showError('Registrierung fehlgeschlagen', data.error || 'Es ist ein Fehler aufgetreten.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      showError('Fehler', 'Ein unerwarteter Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="login-page">
        <ModalComponent />
        <div className="login-container">
          <div className="loading">Überprüfe Link...</div>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="login-page">
        <ModalComponent />
        <div className="login-container">
          <div className="login-header">
            <div className="login-icon" style={{color: 'var(--danger-color)'}}>❌</div>
            <h1>Link Ungültig</h1>
            <p>Dieser Registrierungs-Link kann nicht verwendet werden.</p>
          </div>
          <button onClick={() => navigate('/login')} className="login-button" style={{marginTop: '20px'}}>
            Zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <ModalComponent />
      <div className="login-container register-container">
        <div className="login-header">
          <div className="login-icon">📝</div>
          <h1>Konto Erstellen</h1>
          <p>Bitte geben Sie Ihre Daten ein</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group row">
            <div className="form-col">
              <label htmlFor="firstname">Vorname</label>
              <input
                type="text"
                id="firstname"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="form-col">
              <label htmlFor="lastname">Nachname</label>
              <input
                type="text"
                id="lastname"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">E-Mail Adresse</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@beispiel.de"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Passwort</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
              required
              disabled={loading}
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? '⏳ Registrierung läuft...' : '✅ Konto erstellen'}
          </button>
        </form>

        <div className="login-footer">
          <button type="button" onClick={() => navigate('/login')} className="link-button" disabled={loading}>
            Ich habe bereits ein Konto
          </button>
        </div>
      </div>
    </div>
  );
}
